import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import TopNav from '../components/feature/TopNav';
import MobileFieldWithVerify from '../components/feature/MobileFieldWithVerify';
import { mvpApi } from '../services/api';
import { showPopupMessage } from '../utils/showPopup';
import { capitalizeWords, isStrictEmail, normalizeEmail } from '../utils/formInputUtils';

const LANGUAGE_OPTIONS = [
  { code: 'ES', labelKey: 'advisorPortal.newCustomerLangEs' },
  { code: 'EN', labelKey: 'advisorPortal.newCustomerLangEn' },
  { code: 'AR', labelKey: 'advisorPortal.newCustomerLangAr' },
  { code: 'FR', labelKey: 'advisorPortal.newCustomerLangFr' },
];

function Field({ label, required = false, children, hint = '' }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
        {label}
        {required ? <span className="text-rose-500 ml-0.5">*</span> : null}
      </label>
      {children}
      {hint ? <p className="text-[11px] text-slate-400 mt-1">{hint}</p> : null}
    </div>
  );
}

const inputCls =
  'w-full min-h-[44px] px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition';

const selectCls =
  'w-full min-h-[44px] px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 cursor-pointer';

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden min-w-0">
      <div className="flex items-center gap-3 px-3 py-3 sm:px-5 sm:py-4 border-b border-slate-100 bg-slate-50/60">
        <div className="w-3.5 h-3.5 rounded-[4px] bg-slate-400 shrink-0" />
        <h2 className="text-sm font-semibold text-slate-800 min-w-0 break-words">{title}</h2>
      </div>
      <div className="p-3 sm:p-5 min-w-0">{children}</div>
    </div>
  );
}

export default function AdvisorCustomerNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [saved, setSaved] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [segundoApellido, setSegundoApellido] = useState('');
  const [mobile, setMobile] = useState('');
  const [mobileCountryCode, setMobileCountryCode] = useState('+34');
  const [mobileChannel, setMobileChannel] = useState('SMS');
  const [mobileVerified, setMobileVerified] = useState(false);
  const [mobileVerifying, setMobileVerifying] = useState(false);
  const [email, setEmail] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailVerifying, setEmailVerifying] = useState(false);
  const [emailCode, setEmailCode] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [preferredLanguage, setPreferredLanguage] = useState('ES');
  const [preferredContact, setPreferredContact] = useState('Mobile');
  const [hasAuthorized, setHasAuthorized] = useState(false);
  const [authName, setAuthName] = useState('');
  const [authRelationship, setAuthRelationship] = useState('');
  const [authPhone, setAuthPhone] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [authEmailError, setAuthEmailError] = useState('');
  const [saving, setSaving] = useState(false);

  const isValid = firstName.trim() && lastName.trim();
  const normalizedEmail = normalizeEmail(email);
  const hasInvalidEmail = Boolean(normalizedEmail && !isStrictEmail(normalizedEmail));
  const normalizedAuthEmail = normalizeEmail(authEmail);
  const hasInvalidAuthEmail = Boolean(hasAuthorized && normalizedAuthEmail && !isStrictEmail(normalizedAuthEmail));
  const phoneRaw = String(mobile || '').trim();
  const cc = String(mobileCountryCode || '').trim() || '+34';
  const phone = phoneRaw ? `${cc}${phoneRaw}`.replace(/\s+/g, '') : '';

  const requestPhoneOtp = async () => {
    const resp = await mvpApi.adminRequestPhoneOtp(phone, 'sms');
    return resp?.data;
  };

  const verifyPhoneOtp = async (code) => {
    await mvpApi.adminVerifyPhoneOtp(phone, code);
  };

  const requestEmailOtp = async () => {
    const resp = await mvpApi.adminRequestEmailOtp(normalizedEmail);
    return resp?.data;
  };

  const handleSave = async () => {
    if (!isValid) return;
    if (normalizedEmail && !isStrictEmail(normalizedEmail)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    if (hasAuthorized && authEmail && !isStrictEmail(authEmail)) {
      setAuthEmailError('Please enter a valid email address.');
      return;
    }
    setSaving(true);
    try {
      const preferredContactMethod = preferredContact === 'Email' ? 'email' : 'mobile';
      const preferredContactChannel = 'sms';
      const preferredLanguageCode = String(preferredLanguage || 'ES').toLowerCase();

      const res = await mvpApi.createAdminUser({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        second_surname: String(segundoApellido || '').trim() || undefined,
        email: normalizedEmail || undefined,
        email_verified: normalizedEmail ? emailVerified : undefined,
        phone: phone || undefined,
        phone_verified: phone ? mobileVerified : undefined,
        preferred_language: preferredLanguageCode || undefined,
        preferred_contact_method: preferredContactMethod,
        preferred_contact_channel: preferredContactChannel,
        authorized_contact_name: hasAuthorized ? (String(authName || '').trim() || undefined) : undefined,
        authorized_contact_relationship_to_user: hasAuthorized ? (String(authRelationship || '').trim() || undefined) : undefined,
        authorized_contact_phone: hasAuthorized ? (String(authPhone || '').trim() || undefined) : undefined,
        authorized_contact_email: hasAuthorized ? (String(authEmail || '').trim().toLowerCase() || undefined) : undefined,
      });

      const createdUserId = res?.data?.user_id;
      if (!createdUserId) throw new Error('Missing user id in create response');
      setSaved(true);
      navigate(`/customer/${createdUserId}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || t('advisorPortal.newCustomerCreateFailed');
      showPopupMessage(typeof msg === 'string' ? msg : t('advisorPortal.newCustomerCreateFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col min-w-0 max-w-[100vw] overflow-x-hidden">
      <TopNav />
      <main className="flex-1 w-full min-w-0 max-w-screen-lg mx-auto px-3 py-4 sm:px-5 sm:py-6 md:px-8 md:py-8">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-400 mb-4 sm:mb-6 min-w-0">
          <Link to="/advisor" className="hover:text-slate-700 transition-colors cursor-pointer shrink-0">
            {t('advisorPortal.newCustomerCrumbSearch')}
          </Link>
          <i className="ri-arrow-right-s-line shrink-0" aria-hidden />
          <span className="text-slate-800 font-semibold min-w-0 break-words">{t('advisorPortal.newCustomerTitle')}</span>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-5 sm:mb-6 min-w-0">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-semibold text-slate-900">{t('advisorPortal.newCustomerTitle')}</h1>
            <p className="text-sm text-slate-500 mt-1 max-w-prose">
              {t('advisorPortal.newCustomerSubtitle')}
            </p>
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={!isValid || saving || hasInvalidEmail || hasInvalidAuthEmail}
            className={`inline-flex w-full sm:w-auto min-h-[44px] items-center justify-center gap-1.5 text-sm font-medium px-4 py-2.5 rounded-lg transition-colors cursor-pointer touch-manipulation shrink-0 ${
              isValid && !saving && !hasInvalidEmail && !hasInvalidAuthEmail ? 'bg-slate-900 text-white hover:bg-slate-800' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            {saving ? (
              <><i className="ri-loader-4-line text-sm animate-spin" aria-hidden /> {t('advisorPortal.newCustomerSaving')}</>
            ) : saved ? (
              <><i className="ri-check-line text-sm" aria-hidden /> {t('advisorPortal.newCustomerSaved')}</>
            ) : (
              <><i className="ri-save-line text-sm" aria-hidden /> {t('advisorPortal.newCustomerSave')}</>
            )}
          </button>
        </div>

        <div className="space-y-4 sm:space-y-5 min-w-0">
          <Section title={t('advisorPortal.newCustomerSecPersonal')}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <Field label={t('advisorPortal.newCustomerFirstName')} required>
                <input type="text" value={firstName} onChange={(e) => setFirstName(capitalizeWords(e.target.value))} placeholder={t('advisorPortal.newCustomerPhFirstName')} className={inputCls} />
              </Field>
              <Field label={t('advisorPortal.newCustomerLastName')} required>
                <input type="text" value={lastName} onChange={(e) => setLastName(capitalizeWords(e.target.value))} placeholder={t('advisorPortal.newCustomerPhLastName')} className={inputCls} />
              </Field>
              <Field label={t('advisorPortal.newCustomerSecondSurname')}>
                <input type="text" value={segundoApellido} onChange={(e) => setSegundoApellido(e.target.value)} placeholder={t('advisorPortal.newCustomerPhSecondSurname')} className={inputCls} />
              </Field>
            </div>
          </Section>

          <Section title={t('advisorPortal.newCustomerSecContact')}>
            <div className="space-y-4 w-full max-w-full lg:max-w-2xl min-w-0">
              <Field label={t('advisorPortal.newCustomerMobile')}>
                <MobileFieldWithVerify
                  value={mobile}
                  onChange={(next) => {
                    setMobile(next);
                    if (mobileVerified) setMobileVerified(false);
                  }}
                  countryCode={mobileCountryCode}
                  onCountryCodeChange={(next) => {
                    setMobileCountryCode(next);
                    if (mobileVerified) setMobileVerified(false);
                  }}
                  channel={mobileChannel}
                  onChannelChange={setMobileChannel}
                  verified={mobileVerified}
                  onVerifiedChange={setMobileVerified}
                  onRequestOtp={requestPhoneOtp}
                  onVerifyOtp={verifyPhoneOtp}
                          onError={(msg) => showPopupMessage(msg, 'error')}
                  lockInputsWhileVerifying
                  onVerifyingChange={setMobileVerifying}
                />
              </Field>
              <Field label={t('advisorPortal.newCustomerEmail')}>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch sm:gap-2 min-w-0">
                  <input
                    type="email"
                    value={email}
                    disabled={emailVerifying && !emailVerified}
                    onChange={(e) => {
                      const next = e.target.value;
                      setEmail(next);
                      const normalized = normalizeEmail(next);
                      setEmailError(normalized && !isStrictEmail(normalized) ? 'Please enter a valid email address.' : '');
                      if (emailVerified) setEmailVerified(false);
                    }}
                    onPaste={(e) => {
                      const pasted = e.clipboardData?.getData('text') || '';
                      const normalized = normalizeEmail(`${email}${pasted}`);
                      setEmailError(normalized && !isStrictEmail(normalized) ? 'Please enter a valid email address.' : '');
                    }}
                    placeholder={t('advisorPortal.newCustomerPhEmail')}
                    className={`${inputCls} sm:flex-1 disabled:opacity-60 disabled:cursor-not-allowed`}
                  />
                  <div className="flex flex-wrap items-center gap-2 shrink-0 sm:justify-end">
                  {emailVerified ? (
                    <span className="inline-flex min-h-[40px] items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1.5 rounded-lg whitespace-nowrap">
                      <i className="ri-checkbox-circle-fill text-emerald-500 text-xs shrink-0" aria-hidden />
                      {t('advisorPortal.mobileFieldVerified')}
                    </span>
                  ) : !emailVerifying ? (
                    <button
                      type="button"
                      onClick={async () => {
                        if (!isStrictEmail(email) || emailBusy) return;
                        setEmailBusy(true);
                        try {
                          await requestEmailOtp();
                          setEmailVerifying(true);
                        } catch (err) {
                          const msg = err?.response?.data?.detail || err?.message || t('errors.sendCodeFailed');
                          showPopupMessage(typeof msg === 'string' ? msg : t('errors.sendCodeFailed'), 'error');
                        } finally {
                          setEmailBusy(false);
                        }
                      }}
                      disabled={!isStrictEmail(email) || emailBusy}
                      className="min-h-[44px] text-xs sm:text-[11px] font-semibold text-slate-600 border border-slate-200 bg-white px-3 py-2 rounded-lg hover:border-slate-400 hover:text-slate-800 transition-colors cursor-pointer touch-manipulation whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {emailBusy ? t('common.sending', { defaultValue: 'Sending...' }) : t('advisorPortal.mobileFieldVerify')}
                    </button>
                  ) : null}
                  </div>
                </div>
                {emailError ? <p className="text-[11px] text-rose-600 mt-1">{emailError}</p> : null}
                {emailVerifying && !emailVerified ? (
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:flex-wrap pl-0 sm:pl-1 mt-2 min-w-0">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={emailCode}
                      onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="- - - - - -"
                      maxLength={6}
                      className="w-full max-w-xs min-h-[44px] px-3 py-2 text-base font-mono border border-slate-200 rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400 bg-white text-center tracking-[0.35em] sm:tracking-[0.5em]"
                    />
                    <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={async () => {
                        if (!/^\d{6}$/.test(emailCode) || emailBusy) return;
                        setEmailBusy(true);
                        try {
                          await mvpApi.adminVerifyEmailOtp(normalizedEmail, emailCode);
                          setEmailVerified(true);
                          setEmailVerifying(false);
                          setEmailCode('');
                        } catch (err) {
                          const msg = err?.response?.data?.detail || err?.message || t('errors.verifyCodeFailed');
                          showPopupMessage(typeof msg === 'string' ? msg : t('errors.verifyCodeFailed'), 'error');
                        } finally {
                          setEmailBusy(false);
                        }
                      }}
                      disabled={!/^\d{6}$/.test(emailCode) || emailBusy}
                      className="min-h-[44px] text-xs sm:text-[11px] font-semibold px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 transition-colors cursor-pointer touch-manipulation whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {emailBusy ? t('common.confirming', { defaultValue: 'Confirming...' }) : t('advisorPortal.mobileFieldConfirm')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEmailVerifying(false);
                        setEmailCode('');
                      }}
                      className="min-h-[44px] px-2 text-xs sm:text-[11px] text-slate-400 hover:text-slate-600 cursor-pointer touch-manipulation"
                    >
                      {t('advisorPortal.mobileFieldCancel')}
                    </button>
                    </div>
                  </div>
                ) : null}
                {(mobileVerifying || (emailVerifying && !emailVerified)) ? (
                  <p className="text-[11px] text-slate-500 mt-1">
                    {t('advisorPortal.newCustomerOtpPendingHint', { defaultValue: 'Verify or cancel the current OTP request before editing this contact detail.' })}
                  </p>
                ) : null}
              </Field>
            </div>
          </Section>

          <Section title={t('advisorPortal.preferences')}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label={t('advisorPortal.newCustomerPreferredContact')}>
                <div className="flex items-center gap-1 bg-slate-100 rounded-full p-0.5 w-full max-w-md sm:w-fit min-w-0">
                  {['Mobile', 'Email'].map((opt) => {
                    const mobileFilled = mobile.replace(/\D/g, '').length >= 6;
                    const emailFilled = isStrictEmail(email);
                    const disabled = opt === 'Mobile' ? !mobileFilled : !emailFilled;
                    const selected = preferredContact === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        disabled={disabled}
                        onClick={() => !disabled && setPreferredContact(opt)}
                        className={`text-xs font-semibold px-3 py-2 sm:py-1 rounded-full transition-colors touch-manipulation min-h-[44px] sm:min-h-0 flex items-center justify-center flex-1 sm:flex-initial sm:whitespace-nowrap ${
                          disabled
                            ? 'text-slate-300 cursor-not-allowed'
                            : selected
                              ? 'bg-white text-slate-800 shadow-sm cursor-pointer'
                              : 'text-slate-500 hover:text-slate-700 cursor-pointer'
                        }`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </Field>
              <Field label={t('advisorPortal.newCustomerPreferredLanguage')}>
                <select value={preferredLanguage} onChange={(e) => setPreferredLanguage(e.target.value)} className={selectCls}>
                  {LANGUAGE_OPTIONS.map((l) => (
                    <option key={l.code} value={l.code}>{t(l.labelKey)}</option>
                  ))}
                </select>
              </Field>
            </div>
          </Section>

          <Section title={t('advisorPortal.newCustomerSecAuthorized')}>
            <div className="flex flex-wrap items-start gap-3 mb-4 min-w-0">
              <button
                type="button"
                onClick={() => setHasAuthorized(!hasAuthorized)}
                className="shrink-0 p-2 -m-2 rounded-lg touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40"
                aria-pressed={hasAuthorized}
                aria-label={t('advisorPortal.newCustomerAuthToggle')}
              >
                <span className={`relative block w-10 h-5 rounded-full transition-colors ${hasAuthorized ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${hasAuthorized ? 'left-5' : 'left-0.5'}`} />
                </span>
              </button>
              <span className="text-sm text-slate-600 min-w-0 flex-1 leading-snug">{t('advisorPortal.newCustomerAuthToggle')}</span>
            </div>
            {hasAuthorized ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label={t('advisorPortal.newCustomerAuthContactName')}><input type="text" value={authName} onChange={(e) => setAuthName(e.target.value)} placeholder={t('advisorPortal.newCustomerPhFullName')} className={inputCls} /></Field>
                <Field label={t('advisorPortal.newCustomerAuthRelationship')}><input type="text" value={authRelationship} onChange={(e) => setAuthRelationship(e.target.value)} placeholder={t('advisorPortal.newCustomerPhRelationship')} className={inputCls} /></Field>
                <Field label={t('advisorPortal.newCustomerMobile')}><input type="tel" value={authPhone} onChange={(e) => setAuthPhone(e.target.value)} placeholder={t('advisorPortal.newCustomerPhAuthPhone')} className={inputCls} /></Field>
                <Field label={t('advisorPortal.newCustomerEmail')}>
                  <>
                    <input
                      type="email"
                      value={authEmail}
                      onChange={(e) => {
                        const next = e.target.value;
                        setAuthEmail(next);
                        const normalized = normalizeEmail(next);
                        setAuthEmailError(normalized && !isStrictEmail(normalized) ? 'Please enter a valid email address.' : '');
                      }}
                      onPaste={(e) => {
                        const pasted = e.clipboardData?.getData('text') || '';
                        const normalized = normalizeEmail(`${authEmail}${pasted}`);
                        setAuthEmailError(normalized && !isStrictEmail(normalized) ? 'Please enter a valid email address.' : '');
                      }}
                      placeholder={t('advisorPortal.newCustomerPhEmail')}
                      className={inputCls}
                    />
                    {authEmailError ? <p className="text-[11px] text-rose-600 mt-1">{authEmailError}</p> : null}
                  </>
                </Field>
              </div>
            ) : null}
          </Section>
        </div>
      </main>
    </div>
  );
}
